import {
  BACK_EVENT,
  GOTO_EVENT,
  NEXT_EVENT,
  RECONCILE_EVENT,
  SKIP_EVENT,
  type Wizard,
  type WizardEvent,
  type WizardGuardFn,
  type WizardState,
} from './types';

type GuardImpls = Record<string, WizardGuardFn> | undefined;

function evaluateGuard(name: string, guards: GuardImpls): boolean {
  const guard = guards?.[name];
  if (!guard) {
    throw new Error(`[@heidi/flow] Guard "${name}" has no implementation.`);
  }
  return guard();
}

/**
 * The effective step list: the declared order filtered by each step's
 * `enabled` guard. Consumers never hold their own filtered copy — this
 * derivation is the single source of truth for order, progress, and
 * reconciliation.
 */
export function effectiveSteps<TStep extends string>(
  wizard: Wizard<TStep>,
  guards?: GuardImpls
): TStep[] {
  return wizard.steps
    .filter((step) => step.enabled === undefined || evaluateGuard(step.enabled, guards))
    .map((step) => step.id);
}

/**
 * The wizard's initial reducer state: `initialStep` when seeded (must be a
 * declared id — throws otherwise), else the first *declared* step. Guards are
 * deliberately not evaluated here (so a snapshot-only actor — e.g. the SSR
 * server snapshot — needs no implementations); if the starting step is
 * disabled, the interpreter reconciles immediately on `start()`.
 */
export function initialWizardState<TStep extends string>(
  wizard: Wizard<TStep>,
  initialStep?: TStep
): WizardState<TStep> {
  if (initialStep !== undefined && !wizard.stepIds.includes(initialStep)) {
    throw new Error(
      `[@heidi/flow] Wizard "${wizard.id}": input.step targets unknown step "${initialStep}".`
    );
  }
  return {
    step: initialStep ?? (wizard.stepIds[0] as TStep),
    completed: new Set<TStep>(),
    history: [],
    done: false,
  };
}

function stepConfigOf<TStep extends string>(wizard: Wizard<TStep>, id: TStep) {
  return wizard.steps.find((step) => step.id === id);
}

/**
 * Resolve where `back()` from the current state would land, or `null` when it
 * has nowhere to go. Resolution order: the current step's `backTarget`
 * override, else the most recent visited-history entry, else the previous step
 * in the effective list. Candidates not currently in the effective list are
 * passed over. Shared by the `BACK` reducer branch and `canGoBack`.
 */
export function resolveBackTarget<TStep extends string>(
  wizard: Wizard<TStep>,
  state: WizardState<TStep>,
  list: readonly TStep[]
): TStep | null {
  const config = stepConfigOf(wizard, state.step);
  if (config?.canGoBack === false) return null;
  if (
    config?.backTarget !== undefined &&
    config.backTarget !== state.step &&
    list.includes(config.backTarget)
  ) {
    return config.backTarget;
  }
  for (let i = state.history.length - 1; i >= 0; i -= 1) {
    const candidate = state.history[i] as TStep;
    if (candidate !== state.step && list.includes(candidate)) return candidate;
  }
  const index = list.indexOf(state.step);
  if (index > 0) return list[index - 1] as TStep;
  return null;
}

function without<TStep extends string>(
  completed: ReadonlySet<TStep>,
  step: TStep
): ReadonlySet<TStep> {
  if (!completed.has(step)) return completed;
  const next = new Set(completed);
  next.delete(step);
  return next;
}

function with_<TStep extends string>(
  completed: ReadonlySet<TStep>,
  step: TStep
): ReadonlySet<TStep> {
  if (completed.has(step)) return completed;
  const next = new Set(completed);
  next.add(step);
  return next;
}

/** Truncate the history so everything from `target`'s last visit onward is gone. */
function historyBefore<TStep extends string>(
  history: readonly TStep[],
  target: TStep
): readonly TStep[] {
  const index = history.lastIndexOf(target);
  return index >= 0 ? history.slice(0, index) : history;
}

/**
 * Pure reducer: given a wizard state and an event, return the next state.
 * Returns the *same* state reference on refused or no-op navigation (failed
 * `validate` guard, `skip()` on a non-skippable step, `back()` with nowhere to
 * go, any event once `done`) — the interpreter uses reference identity to
 * decide whether a transition was taken.
 */
export function transitionWizard<TStep extends string>(
  wizard: Wizard<TStep>,
  state: WizardState<TStep>,
  event: WizardEvent<TStep>,
  impls?: { guards?: Record<string, WizardGuardFn> }
): WizardState<TStep> {
  if (state.done) return state;
  const list = effectiveSteps(wizard, impls?.guards);
  const index = list.indexOf(state.step);
  const config = stepConfigOf(wizard, state.step);

  switch (event.type) {
    case NEXT_EVENT: {
      if (index === -1) return state;
      if (config?.validate && !evaluateGuard(config.validate, impls?.guards)) {
        return state;
      }
      const completed = with_(state.completed, state.step);
      const target = list[index + 1];
      if (target === undefined) return { ...state, completed, done: true };
      return {
        step: target,
        completed,
        history: [...state.history, state.step],
        done: false,
      };
    }

    case SKIP_EVENT: {
      if (index === -1 || config?.skippable !== true) return state;
      const target = list[index + 1];
      if (target === undefined) return { ...state, done: true };
      return { ...state, step: target, history: [...state.history, state.step] };
    }

    case BACK_EVENT: {
      const target = resolveBackTarget(wizard, state, list);
      if (target === null) return state;
      return {
        step: target,
        completed: without(state.completed, target),
        history: historyBefore(state.history, target),
        done: false,
      };
    }

    case GOTO_EVENT: {
      const target = event.step;
      if (!wizard.stepIds.includes(target)) {
        throw new Error(
          `[@heidi/flow] Wizard "${wizard.id}": goTo targets unknown step "${target}".`
        );
      }
      if (target === state.step || !list.includes(target)) return state;
      const forward = index !== -1 && list.indexOf(target) > index;
      if (forward) {
        if (config?.validate && !evaluateGuard(config.validate, impls?.guards)) {
          return state;
        }
        return {
          ...state,
          step: target,
          history: [...state.history, state.step],
        };
      }
      return { ...state, step: target, history: historyBefore(state.history, target) };
    }

    case RECONCILE_EVENT: {
      if (index !== -1) return state;
      // The current step fell out of the effective list. Policy decides the
      // direction: 'forward' (default) advances to the next enabled step in
      // *declared* order, completing when none follows — right for flows
      // where a vanished step means its work is obsolete (a firmware step
      // after the update lands). 'backward' retreats to the nearest earlier
      // enabled step, falling forward only when nothing earlier survives —
      // right for form flows where later steps depend on data the user
      // hasn't entered yet.
      const declaredIndex = wizard.stepIds.indexOf(state.step);
      if (wizard.reconcile === 'backward') {
        for (let i = declaredIndex - 1; i >= 0; i -= 1) {
          const id = wizard.stepIds[i];
          if (id !== undefined && list.includes(id)) return { ...state, step: id };
        }
      }
      const target = wizard.stepIds
        .slice(declaredIndex + 1)
        .find((id) => list.includes(id));
      if (target !== undefined) return { ...state, step: target };
      return { ...state, done: true };
    }

    default:
      return state;
  }
}
